import React from 'react';
import { Modal, ModalActions, ModalContent, ModalTitle } from '../Modal';
import { AsyncButton, Button } from '../Button';


type ConfirmationItem = {
  id: number;
  options: {
    title?: React.ReactNode;
    description?: React.ReactNode;
    severity?: 'secondary' | 'error' | 'warning';
    confirmButtonText?: string;
    onConfirm: () => Promise<void>;
  };
};


const ConfirmationsContext = React.createContext<{
  confirmations: ConfirmationItem[];
  setConfirmations: React.Dispatch<React.SetStateAction<ConfirmationItem[]>>;
} | null>(null);


const useConfirmationsContext = () => {
  const context = React.useContext(ConfirmationsContext);

  if (!context) {
    throw new Error('useConfirmationsContext must be used within an ConfirmationProvider');
  }

  return context;
};


const ConfirmationsContainer: React.FC = () => {
  const confirmationsContext = useConfirmationsContext();

  return (
    <>
      {confirmationsContext.confirmations.map((confirmation) => (
        <Modal key={confirmation.id} open maxWidth="sm" fullWidth>
          {confirmation.options.title && (
            <ModalTitle>
              {confirmation.options.title}
            </ModalTitle>
          )}

          {confirmation.options.description && (
            <ModalContent>
              {confirmation.options.description}
            </ModalContent>
          )}

          <ModalActions>
            <Button
              fullWidth
              onClick={() => {
                confirmationsContext.setConfirmations(_confirmations => {
                  return _confirmations.filter(c => c.id !== confirmation.id);
                });
              }}
            >
              Cancel
            </Button>
            <AsyncButton
              fullWidth
              variant="contained"
              color={confirmation.options.severity}
              onClick={async () => {
                await confirmation.options.onConfirm().finally(() => {
                  confirmationsContext.setConfirmations(_confirmations => {
                    return _confirmations.filter(c => c.id !== confirmation.id);
                  });
                });
              }}
            >
              {confirmation.options.confirmButtonText ?? 'Confirm'}
            </AsyncButton>
          </ModalActions>
        </Modal>
      ))}
    </>
  );
};


export const ConfirmationProvider: React.FC = ({ children }) => {
  const [ alerts, setAlerts ] = React.useState<ConfirmationItem[]>([]);

  return (
    <ConfirmationsContext.Provider
      value={{
        confirmations: alerts,
        setConfirmations: setAlerts,
      }}
    >
      {children}
      <ConfirmationsContainer />
    </ConfirmationsContext.Provider>
  );
};


export const useConfirmation = () => {
  const context = useConfirmationsContext();
  const contextRef = React.useRef(context);

  contextRef.current = context;

  return React.useMemo(() => {
    const context = contextRef.current;

    return {
      confirm: (options: ConfirmationItem['options']) => {
        const item = {
          id: new Date().getTime(),
          options: {
            ...options,
            severity: options?.severity ?? 'secondary',
          }
        };

        context.setConfirmations(_confirmations => {
          return [ ..._confirmations, item ];
        });


        return item.id;
      },
    };
  }, []);
};
